/**
 * messagingSpec — Slack + Teams + Google Chat + WhatsApp.
 *
 * v1 source-of-truth:
 *   ando-frontend/app/pages/SlackPage.tsx (232)
 *   ando-frontend/app/pages/TeamsPage.tsx (232)
 *   ando-frontend/app/pages/GoogleChatPage.tsx (243)
 *   ando-frontend/app/pages/WhatsAppPage.tsx (682 — Embedded Signup flow)
 *
 * Each v1 page is bespoke; the common shape is OAuth → ChatProviderCard +
 * channel/team filter. WhatsApp is the outlier (Meta Embedded Signup is
 * popup + postMessage, not OAuth redirect, so it doesn't fit
 * ConnectAccountDialog). The capability detail route
 * (``routes/_authed/workspace/integrations/$capability.tsx``) mounts
 * ``<WhatsAppConnectorPanel>`` inline for the messaging capability and
 * intercepts the AddAccountMenu "whatsapp" pick to scroll there instead
 * of opening the dialog. Spec stays vendor-list-only — no custom slot.
 */

import type { BaseConnection, CapabilityWizardSpec } from "@ando/ui";
import { i18n as i18nSingleton } from "@lingui/core";
import { msg } from "@lingui/core/macro";
import { Hash, MessageSquare, Shield, Zap } from "lucide-react";

import { createCapabilityApi } from "../api/capabilityApi";
import { vendorIcon } from "../components/vendorIcons";

export interface MessagingConnection extends BaseConnection {
  readonly provider?: string; // slack | teams | google_chat | whatsapp
  readonly display_name?: string;
  readonly workspace_id?: string;
  readonly conversations_synced?: number;
}

const iconCls = "h-4 w-4";

export const messagingSpec: CapabilityWizardSpec<MessagingConnection> = {
  capability: "messaging",
  title: msg`Messaggistica`,
  subtitle: msg`Slack, Teams, Google Chat, WhatsApp — conversazioni e trascrizioni delle riunioni indicizzate`,
  icon: <MessageSquare className="h-5 w-5" aria-hidden />,
  gdprDeleteWindowMinutes: 30,

  vendors: [
    { id: "slack", label: "Slack", icon: vendorIcon("slack", iconCls) },
    {
      id: "teams",
      label: "Microsoft Teams",
      icon: vendorIcon("teams", iconCls),
    },
    {
      id: "google_chat",
      label: "Google Chat",
      icon: vendorIcon("google_chat", iconCls),
    },
    {
      id: "whatsapp",
      label: "WhatsApp Business",
      icon: vendorIcon("whatsapp", iconCls),
      description: msg`Richiede Meta Embedded Signup + opt-in business`,
    },
  ],

  // ``vendor_picker`` retired May 24 2026 — picker is now AddAccountMenu.
  steps: [
    {
      id: "authorize",
      kind: "oauth",
      title: msg`Autorizza`,
      description: msg`Ando indicizza i canali, i thread e le trascrizioni delle riunioni (Teams · Google Meet) a cui l'utente ha accesso.`,
      oauth: { buttonLabel: msg`Autorizza` },
    },
  ],

  benefits: [
    {
      icon: <MessageSquare className={iconCls} aria-hidden />,
      title: msg`Ricerca cross-canale`,
      body: msg`"Cosa abbiamo deciso con il team backend?" — risposta con citazioni.`,
    },
    {
      icon: <Zap className={iconCls} aria-hidden />,
      title: msg`Riepilogo conversazioni`,
      body: msg`Summary del thread + action items in un colpo.`,
    },
    {
      icon: <MessageSquare className={iconCls} aria-hidden />,
      title: msg`Trascrizioni riunioni`,
      body: msg`Le riunioni Teams e Google Meet vengono trascritte e indicizzate: chiedi "cosa è stato deciso?" con citazioni.`,
    },
    {
      icon: <Hash className={iconCls} aria-hidden />,
      title: msg`Filtra canali`,
      body: msg`Decidi quali canali sincronizzare (privacy granulare).`,
    },
    {
      icon: <Shield className={iconCls} aria-hidden />,
      title: msg`Tokens criptati`,
      body: msg`Mai esposti al modello; minimo scope OAuth richiesto.`,
    },
  ],

  api: createCapabilityApi<MessagingConnection>({ capability: "messaging" }),
  connectionKeyField: "id" as keyof MessagingConnection,
  getDisplayName: (c) =>
    c.display_name ?? c.workspace_id ?? c.id ?? i18nSingleton._(msg`Workspace`),
  getSecondaryId: (c) => (c.provider ? c.provider.toUpperCase() : null),
};
